import React, { useState, useEffect, useRef, useCallback } from 'react';

import * as echarts from 'echarts';
import 'echarts-gl';
import { RefreshIcon } from '@heroicons/react/outline';

// Removed: import 'echarts-gl'; // This import caused a resolution error. Assuming echarts-gl is globally available or linked by the environment.

// Tailwind CSS is assumed to be available, no explicit import needed here.

// Custom styles for the app, mirroring the original HTML
const appStyles = `
// body {
//     font-family: 'Inter', sans-serif;
//     background-color: #f0f2f5;
// }
.chart-container {
    background-color: #ffffff;
    border-radius: 0.75rem;
    padding: 0.5rem;
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
    transition: all 0.3s ease-in-out;
    height: 400px; /* Responsive height for chart containers */
    display: flex;
    flex-direction: column;
    
}
@media (prefers-color-scheme: dark) {
    .chart-container {
        background-color: #000; /* Tailwind's gray-800 */
        color: #f9fafb; /* Tailwind's gray-100 */
    }
}

@media (min-width: 1024px) {
    .chart-container {
        height: 480px;
    }
}
.chart-container:hover {
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -2px rgb(0 0 0 / 0.1);
}
.header {
    background-color: #1a202c;
    color: white;
}
.stat-card {
    background: linear-gradient(135deg, #9e1414 0%, #764ba2 100%);
}
.voc-stat-card {
    background: linear-gradient(135deg, #38b2ac 0%, #3182ce 100%);
}
.ai-insight-card {
    background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
}
.echart {
    width: 100%;
    flex-grow: 1; /* Make chart fill container height */
}
/* Removed tab-content display rules from here as components are conditionally rendered */
/* .tab-content {
    display: none;
}
.tab-content.active {
    display: block;
} */
.tab-button {
    padding: 0.75rem 1.5rem;
    font-weight: 600;
    color: #a0aec0;
    border-bottom: 2px solid transparent;
    transition: all 0.3s;
    cursor: pointer;
}
.tab-button.active {
    color: #ffffff;
    border-bottom-color: #667eea;
}
`;

// Helper Function: Formats large numbers for monetary values
const formatLargeMonetary = (num) => {
    if (num === null || num === undefined) return '$0';
    if (num >= 1000000) {
        return '$' + (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
        return '$' + (num / 1000).toFixed(1) + 'K';
    }
    return '$' + num.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

// ===================================================================
// RFM Data Utilities
// ===================================================================

// Generates raw customer data with Recency, Frequency, Monetary values
// const generateRfmRawData = (count) => {
//     const data = [];
//     for (let i = 0; i < count; i++) {
//         data.push({
//             id: i,
//             recency: Math.floor(Math.random() * 365) + 1, // Days since last purchase
//             frequency: Math.floor(Math.random() * 50) + 1, // Total purchases
//             monetary: parseFloat((Math.random() * 20000 + 50).toFixed(2)), // Total spend
//         });
//     }
//     return data;
// };

// Scores customers based on a given metric (Recency, Frequency, Monetary)
const scoreRfmMetric = (dataset, value, metric, ascending = true) => {
    const sortedUniqueValues = [...new Set(dataset.map(c => c[metric]))].sort((a, b) => a - b);
    const percentile = sortedUniqueValues.indexOf(value) / (sortedUniqueValues.length - 1);
    const scoreValue = Math.min(Math.floor(percentile * 5) + 1, 5); // Score 1-5
    return ascending ? scoreValue : 6 - scoreValue; // Recency is inverse (lower days = higher score)
};

// Assigns a segment based on R and F scores
const getRfmSegment = (r, f) => {
    if (r >= 4 && f >= 4) return 'Champions';
    if (r >= 3 && f >= 4) return 'Loyal';
    if (r >= 4 && f >= 2) return 'Potential Loyalists';
    if (r >= 5 && f === 1) return 'New Customers';
    if (r >= 3 && f >= 3) return 'Promising';
    if (r >= 2 && f >= 2) return 'Needs Attention';
    if (r <= 2 && f >= 4) return 'At Risk';
    if (r <= 2 && f <= 2) return 'Hibernating';
    return 'Lost';
};

// Processes raw data to add RFM scores and segments
const processRfmData = (rawData) => {
    return rawData.map(c => {
        const R = scoreRfmMetric(rawData, c.Recency, 'Recency', false);
        const F = scoreRfmMetric(rawData, c.Frequency, 'Frequency', true);
        const M = scoreRfmMetric(rawData, c.Monetary, 'Monetary', true);
        return { ...c, R, F, M, Segment: getRfmSegment(R, F) };
    });
};

// ===================================================================
// VoC Data Utilities
// ===================================================================

const sentiments = ['Positive', 'Negative', 'Neutral'];
const vocAllTopics = ['Product Quality', 'Customer Service', 'Price', 'Website UX', 'Shipping', 'Battery Life'];
const vocAllFeatures = ['Camera', 'Screen', 'Performance', 'Design', 'Value'];
const vocAllProducts = ['Galaxy S25 Ultra', 'Galaxy S23', 'iPhone 15 Pro', 'Pixel Pro 8', 'Galaxy Z Fold 5', 'iPhone 16'];
const sentimentScores = { Positive: 1, Neutral: 0, Negative: -1 };
const sentimentColors = { Positive: "#22c55e", Negative: "#ef4444", Neutral: "#facc15" };

// Generates voice of customer data
const generateVocData = (count) => {
    const data = [];
    for (let i = 0; i < count; i++) {
        const date = new Date();
        // Generate dates within the last 90 days
        date.setDate(date.getDate() - Math.floor(Math.random() * 90));
        data.push({
            id: i,
            date: date.toISOString().split("T")[0],
            sentiment: sentiments[Math.floor(Math.random() * sentiments.length)],
            topic: vocAllTopics[Math.floor(Math.random() * vocAllTopics.length)],
            feature: vocAllFeatures[Math.floor(Math.random() * vocAllFeatures.length)],
            product: vocAllProducts[Math.floor(Math.random() * vocAllProducts.length)],
        });
    }
    return data;
};

// ===================================================================
// Reusable Chart Component
// This component abstracts ECharts initialization and resizing.
// ===================================================================

const EChartComponent = ({ id, title, option, onChartClick, resetRfmFilters }) => {
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null); // Ref to store the ECharts instance


    // Effect for ECharts initialization and event listeners (runs once on mount)
    useEffect(() => {
        if (chartRef.current) {
            chartInstanceRef.current = echarts.init(chartRef.current);

            // Attach click event listener if provided
            if (onChartClick) {

                // Ensure the event listener is added only once and is stable
                chartInstanceRef.current.on('click', onChartClick);
            }
        }

        // Cleanup function for when the component unmounts
        return () => {
            if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed()) {
                chartInstanceRef.current.dispose();
            }
            chartInstanceRef.current = null; // Clear the ref
        };
    }, []); // Empty dependency array: runs only once on mount

    // Effect for handling chart options updates
    useEffect(() => {
        if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed()) {
            chartInstanceRef.current.setOption(option, true);
        }
    }, [option]); // Rerun when 'option' prop changes

    // Effect for ResizeObserver (runs once on mount)
    useEffect(() => {
        const currentChartDom = chartRef.current;
        const currentChartInstance = chartInstanceRef.current; // Capture the ECharts instance

        const resizeObserver = new ResizeObserver(() => {
            if (currentChartInstance && !currentChartInstance.isDisposed()) {
                currentChartInstance?.resize();
            }
        });

        if (currentChartDom) {
            resizeObserver.observe(currentChartDom);
        }

        // Cleanup for ResizeObserver
        return () => {
            if (currentChartDom) {
                resizeObserver.unobserve(currentChartDom);
                resizeObserver.disconnect();
            }
        };
    }, []); // Empty dependency array: runs only once on mount

    return (
        <div className="chart-container mb-4">
            <div className="flex items-center justify-between mb-4 ">
                <h2 className="text-xl font-semibold ml-5 mb-4 text-gray-700 dark:text-white">{title}</h2>
                <div onClick={resetRfmFilters}    >
                    <RefreshIcon
                        onClick={(e) => {

                            resetRfmFilters();
                        }}
                        className="h-5 w-5 mr-2 text-gray-900 dark:text-white hover:text-indigo-600 cursor-pointer transition-colors" />
                </div>

            </div>

            {/* <div 
            style={{ minHeight: 400, minWidth: 500 }} 
            id={id} 
            ref={chartRef} 
            className="echart"></div> */}


            <div className="flex items-center justify-center">
                <div
                    id={id}
                    ref={chartRef}
                    className="echart w-full dark:bg-black"
                    style={{
                        minHeight: window.innerWidth < 568 ? '300px' : '400px',
                        width: window.innerWidth < 568 ? '300px' : '100%',
                    }}
                ></div>
            </div>




        </div >
    );
};


// ===================================================================
// RFM Dashboard Components
// ===================================================================

const RfmAiInsight = ({ filteredData, activeFilters, masterData }) => {
    const totalCustomerCount = masterData.length;
    const totalSales = masterData.reduce((s, d) => s + d.Monetary, 0);

    let insight = "Select a filter to see insights.";

    if (Object.values(activeFilters).some(f => f)) {
        if (filteredData.length < 1) {
            insight = "No customers match the selected filters.";
        } else {
            const filteredSales = filteredData.reduce((s, d) => s + d.Monetary, 0);
            const salesContribution = (filteredSales / totalSales * 100).toFixed(1);
            const customerContribution = (filteredData.length / totalCustomerCount * 100).toFixed(1);

            if (activeFilters.Segment) {
                insight = `'${activeFilters.Segment}' make up ${customerContribution}% of customers and contribute ${formatLargeMonetary(filteredSales)} (${salesContribution}%) of total sales. `;
                const avgMonetary = filteredSales / filteredData.length;
                const overallAvg = totalSales / totalCustomerCount;
                if (avgMonetary > overallAvg) insight += `Their average spend is higher than the overall average.`;
                else insight += `Their average spend is lower than the overall average.`;
            } else if (activeFilters.R && activeFilters.F) {
                insight = `Customers with R=${activeFilters.R} & F=${activeFilters.F} represent ${customerContribution}% of customers. `;
                const segmentCounts = filteredData.reduce((a, d) => { a[d.Segment] = (a[d.Segment] || 0) + 1; return a; }, {});
                const topSegment = Object.keys(segmentCounts).sort((a, b) => segmentCounts[b] - segmentCounts[a])[0];
                insight += `This group is mostly composed of '${topSegment}' customers, contributing ${formatLargeMonetary(filteredSales)}.`;
            } else {
                insight = `The selected ${filteredData.length} customers account for ${formatLargeMonetary(filteredSales)} (${salesContribution}%) of total revenue.`;
            }
        }
    }

    return (
        <div className="ai-insight-card text-white p-6 rounded-xl shadow-lg col-span-2 md:col-span-3 lg:col-span-2">
            <div className="flex justify-between items-center mb-2">
                <div className="text-sm font-medium text-indigo-200">Generated Insight</div>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-indigo-300"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1-1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" /></svg>
            </div>
            <div id="rfm-ai-insight-text" className="text-base font-light h-16 overflow-y-auto">{insight}</div>
        </div>
    );
};

const RfmDashboard = ({ apiData, onFilterChange, rfmFilters, resetRfmFilters }) => {
    const [masterData, setMasterData] = useState([]);

    // Initialize master data on mount
    useEffect(() => {
        //const rawData = generateRfmRawData(2000);
        //setMasterData(processRfmData(rawData));

        setMasterData(apiData.customers)


    }, []);

    // Filter data based on active filters
    const filteredData = masterData.filter(d =>
        (rfmFilters.R ? d.R === rfmFilters.R : true) &&
        (rfmFilters.F ? d.F === rfmFilters.F : true) &&
        (rfmFilters.M ? d.M === rfmFilters.M : true) &&
        (rfmFilters.Segment ? d.Segment === rfmFilters.Segment : true)
    );

    // KPI Calculations
    const totalCustomers = masterData.length;
    const totalSales = masterData.reduce((s, d) => s + d.Monetary, 0);
    const averageRecency = masterData.length > 0 ? Math.round(masterData.reduce((s, d) => s + d.Recency, 0) / masterData.length) : 0;
    const selectedCustomers = filteredData.length;
    const hasActiveFilter = Object.values(rfmFilters).some(f => f !== null);

    // Common segment colors
    const segmentColors = { 'Champions': '#f58231', 'Loyal': '#4363d8', 'New Customers': '#f032e6', 'Promising': '#fff000', 'At Risk': '#e6194b', 'Hibernating': '#facc15', 'Lost': '#71717a' };

    // Chart options (memoized for performance)
    const rfmGridOption = useCallback(() => {
        const gridData = Array.from({ length: 5 }, () => Array(5).fill(0));
        filteredData.forEach(d => { gridData[d.R - 1][d.F - 1]++; });
        const gridHeatmapData = gridData.flatMap((row, r) => row.map((val, f) => [f, r, val])).filter(d => d[2] > 0);
        return {
            tooltip: {
                formatter: (p) => `R=${p.value[1] + 1}, F=${p.value[0] + 1}<br>Customers: ${p.value[2]}`
            },
            xAxis: {
                type: 'category', data: ['F1', 'F2', 'F3', 'F4', 'F5'],
                axisLabel: {
                    color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#eee' : '#666',
                }
            },
            yAxis: {
                type: 'category', data: ['R1', 'R2', 'R3', 'R4', 'R5'],
                axisLabel: {
                    color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#eee' : '#666',
                }
            },
            visualMap: {
                min: 0,
                max: echarts.util.reduce(gridHeatmapData, (max, item) => Math.max(max, item[2]), 0),
                calculable: true,
                orient: 'horizontal',
                left: 'center',
                bottom: '5%'
            },
            series: [{
                name: 'Customers',
                type: 'heatmap',
                data: gridHeatmapData,
                label: { show: true },
                emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.5)' } }
            }]
        };
    }, [filteredData]);

    const orderCountOption = useCallback(() => {
        const orderBins = { '1': 0, '2-5': 0, '6-10': 0, '11-20': 0, '21+': 0 };
        filteredData.forEach(d => {
            if (d.Frequency === 1) orderBins['1']++;
            else if (d.Frequency <= 5) orderBins['2-5']++;
            else if (d.Frequency <= 10) orderBins['6-10']++;
            else if (d.Frequency <= 20) orderBins['11-20']++;
            else orderBins['21+']++;
        });
        return {
            tooltip: { trigger: 'axis' },
            xAxis: {
                type: 'category', data: Object.keys(orderBins),
                axisLabel: {
                    color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#eee' : '#666',
                }
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    fontSize: window.innerWidth < 568 ? 10 : 12,
                    color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#eee' : '#666',
                    formatter: (value) => {
                        if (value >= 1000000) {
                            return (value / 1000000).toFixed(1) + 'M';
                        } else if (value >= 1000) {
                            return (value / 1000).toFixed(1) + 'K';
                        }
                        return value;
                    }
                },
            },
            series: [{ type: 'bar', data: Object.values(orderBins), itemStyle: { color: '#667eea' } }]
        };
    }, [filteredData]);

    const segmentsChartOption = useCallback(() => {
        const segmentCounts = filteredData.reduce((a, d) => { a[d.Segment] = (a[d.Segment] || 0) + 1; return a; }, {});
        const sortedSegments = Object.keys(segmentCounts).sort((a, b) => segmentCounts[b] - segmentCounts[a]);
        return {
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
            xAxis: {
                type: 'category', data: sortedSegments,
                axisLabel: { interval: 0, rotate: 45, fontSize: 8, color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#eee' : '#666', }
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    fontSize: window.innerWidth < 568 ? 8 : 12,
                    color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#eee' : '#666',
                }
            }
            ,
            series: [{
                data: sortedSegments.map(s => ({
                    value: segmentCounts[s],
                    name: s,
                    itemStyle: { color: rfmFilters.Segment === s ? '#4f46e5' : segmentColors[s] }
                })),
                type: 'bar'
            }]
        };
    }, [filteredData, rfmFilters.Segment]);

    const monetarySegmentChartOption = useCallback(() => {
        const segmentCounts = filteredData.reduce((a, d) => { a[d.Segment] = (a[d.Segment] || 0) + 1; return a; }, {});
        const sortedSegments = Object.keys(segmentCounts).sort((a, b) => segmentCounts[b] - segmentCounts[a]);
        const salesBySegment = sortedSegments.map(seg => ({ name: seg, value: filteredData.filter(d => d.Segment === seg).reduce((a, b) => a + b.Monetary, 0) }));
        return {
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, formatter: p => `${p[0].name}: ${formatLargeMonetary(p[0].value)}` },
            xAxis: {
                type: 'category', data: salesBySegment.map(d => d.name),
                axisLabel: {
                    interval: 0, rotate: 45, fontSize: 8,
                    color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#eee' : '#666',
                }
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    fontSize: window.innerWidth < 568 ? 7 : 12,
                    formatter: p => formatLargeMonetary(p),
                    color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#eee' : '#666',
                }

            },
            series: [{
                data: salesBySegment.map(d => ({
                    value: d.value,
                    name: d.name,
                    itemStyle: { color: rfmFilters.Segment === d.name ? '#4f46e5' : segmentColors[d.name] }
                })),
                type: 'bar'
            }]
        };
    }, [filteredData, rfmFilters.Segment]);

    // const fmHeatmapChartOption = useCallback(() => {
    //     const fMax = Math.max(1, ...filteredData.map(d => d.Frequency));
    //     const mMax = Math.max(1, ...filteredData.map(d => d.Monetary));
    //     const fBuckets = 5, mBuckets = 5;
    //     const fmHeatmapRaw = Array.from({ length: mBuckets }, () => Array(fBuckets).fill(0));
    //     filteredData.forEach(d => {
    //         const fIdx = Math.min(Math.floor(d.Frequency / fMax * fBuckets), fBuckets - 1);
    //         const mIdx = Math.min(Math.floor(d.Monetary / mMax * mBuckets), mBuckets - 1);
    //         fmHeatmapRaw[mIdx][fIdx]++;
    //     });
    //     const fmHeatmapData = fmHeatmapRaw.flatMap((row, m) => row.map((val, f) => [f, m, val])).filter(d => d[2] > 0);
    //     return {
    //         tooltip: {},
    //         grid: { height: '70%', top: '10%' },
    //         xAxis: { type: 'category', data: Array.from({ length: fBuckets }, (_, i) => `${Math.round(i * fMax / fBuckets)}-${Math.round((i + 1) * fMax / fBuckets)} Freq`) },
    //         yAxis: { type: 'category', data: Array.from({ length: mBuckets }, (_, i) => `${formatLargeMonetary(i * mMax / mBuckets)}-${formatLargeMonetary((i + 1) * mMax / mBuckets)}`) },
    //         visualMap: {
    //             min: 0,
    //             max: echarts.util.reduce(fmHeatmapData, (m, i) => Math.max(m, i[2]), 0),
    //             calculable: true,
    //             orient: 'horizontal',
    //             left: 'center',
    //             bottom: '2%'
    //         },
    //         series: [{ type: 'heatmap', data: fmHeatmapData, label: { show: true }, emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.5)' } } }]
    //     };
    // }, [filteredData]);

    const fmHeatmapChartOption = useCallback(() => {
        // Data for the heatmap based on F and M counts
        // const gridData = Array.from({ length: 5 }, () => Array(5).fill(0));
        // filteredData.forEach(d => {
        //     // Ensure scores are within 1-5 range for array indexing
        //     const fIdx = Math.max(0, Math.min(4, d.F - 1));
        //     const mIdx = Math.max(0, Math.min(4, d.M - 1));
        //     gridData[mIdx][fIdx]++; // M on Y, F on X
        // });

        // const fmHeatmapData = gridData.flatMap((row, m) =>
        //     row.map((val, f) => [f, m, val]) // [F-1, M-1, count]
        // ).filter(d => d[2] > 0); // Only include cells with customers

        const fMax = Math.max(1, ...filteredData.map(d => d.Frequency));
        const mMax = Math.max(1, ...filteredData.map(d => d.Monetary));
        const fBuckets = 5, mBuckets = 5;
        const fmHeatmapRaw = Array.from({ length: mBuckets }, () => Array(fBuckets).fill(0));
        filteredData.forEach(d => {
            const fIdx = Math.min(Math.floor(d.Frequency / fMax * fBuckets), fBuckets - 1);
            const mIdx = Math.min(Math.floor(d.Monetary / mMax * mBuckets), mBuckets - 1);
            fmHeatmapRaw[mIdx][fIdx]++;
        });
        const fmHeatmapData = fmHeatmapRaw.flatMap((row, m) => row.map((val, f) => [f, m, val])).filter(d => d[2] > 0);

        return {
            tooltip: {
                formatter: (p) => {
                    const F = p.value[0] + 1;
                    const M = p.value[1] + 1;
                    const count = p.value[2];
                    return `F=${F}, M=${M}<br>Customers: ${count}`;
                }
            },
            xAxis: {
                type: 'category',
                data: ['F1', 'F2', 'F3', 'F4', 'F5'],
                axisLabel: { interval: 0, rotate: 0 }
            },
            yAxis: {
                type: 'category',
                data: ['M1', 'M2', 'M3', 'M4', 'M5'],
                axisLabel: { interval: 0, rotate: 0 }
            },
            visualMap: {
                min: 0,
                max: echarts.util.reduce(fmHeatmapData, (max, item) => Math.max(max, item[2]), 0),
                calculable: true,
                orient: 'horizontal',
                left: 'center',
                bottom: '5%'
            },
            series: [{
                name: 'Customers',
                type: 'heatmap',
                data: fmHeatmapData,
                label: { show: true },
                emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.5)' } }
            }]
        };
    }, [filteredData]);

    // NEW: Treemap Chart Option
    const treemapChartOption = useCallback(() => {
        const segmentsData = {};

        // Group by Segment and then by R-F score combo
        filteredData.forEach(customer => {
            if (!segmentsData[customer.Segment]) {
                segmentsData[customer.Segment] = {
                    Monetary: 0,
                    children: {}
                };
            }
            segmentsData[customer.Segment].Monetary += customer.Monetary;


            const rfmCombo = `R${customer.R}F${customer.F}M${customer.M}`;
            if (!segmentsData[customer.Segment].children[rfmCombo]) {
                segmentsData[customer.Segment].children[rfmCombo] = {
                    Monetary: 0,
                    count: 0
                };
            }
            segmentsData[customer.Segment].children[rfmCombo].Monetary += customer.Monetary;
            segmentsData[customer.Segment].children[rfmCombo].count += 1;
        });

        // Convert to ECharts treemap format
        const treemapData = Object.keys(segmentsData).map(segmentName => {
            const children = Object.keys(segmentsData[segmentName].children).map(rfmCombo => {
                const comboData = segmentsData[segmentName].children[rfmCombo];
                return {
                    name: rfmCombo,
                    value: comboData.Monetary, // Value for treemap node size
                    parent: segmentName,
                    itemStyle: {
                        // Highlight if this specific R/F combo is filtered
                        color: (rfmFilters.R === parseInt(rfmCombo[1]) &&
                            rfmFilters.F === parseInt(rfmCombo[3]) &&
                            rfmFilters.M === +rfmCombo[5]) ? '#1e40af' : null // A darker blue for sub-selection
                    },
                    label: {
                        show: true,
                        formatter: `{b}`, // \n(${comboData.count} customers) Show combo and customer count
                        color: '#fff',
                        position: 'inside',
                        fontSize: 10,
                    },
                    tooltip: {
                        formatter: () => `${rfmCombo} in ${segmentName}: ${formatLargeMonetary(comboData.Monetary)} (${comboData.count} customers)`
                    }
                };
            }).sort((a, b) => b.value - a.value); // Sort children by Monetary value

            return {
                name: segmentName,
                value: segmentsData[segmentName].Monetary,
                itemStyle: {
                    // Highlight the selected Segment
                    color: rfmFilters.Segment === segmentName ? '#4f46e5' : segmentColors[segmentName] || '#667eea'
                },
                label: {
                    show: true,
                    formatter: `{b}`, // Only show Segment name for parent node
                    color: '#fff',
                    position: 'inside',
                    fontSize: 14,
                    fontWeight: 'bold',
                },
                children: children,

            };
        }).sort((a, b) => b.value - a.value); // Sort segments by Monetary value

        return {
            tooltip: {
                trigger: 'item',
                formatter: (params) => {
                    const { name, value, data, treePathInfo } = params;

                    // Build full path, skipping the root node
                    const path = treePathInfo.map(node => node.name).slice(1).join(' / ');

                    // Optional extra info
                    const count = data?.customerCount ?? '–';
                    const monetary = formatLargeMonetary?.(value) ?? value;

                    return ` <strong>${path}</strong><br/>  Monetary: ${monetary}<br/> Customers: ${count} `;
                }
            },
            series: [
                {
                    type: 'treemap',
                    roam: false, // Disable zooming/panning
                    nodeClick: false, // No default action for node click (we handle it in EChartComponent's on click)
                    breadcrumb: {
                        show: false // Hide breadcrumb navigation
                    },
                    data: treemapData,
                    levels: [
                        { // Level 0: Segments
                            itemStyle: {
                                borderWidth: 0,
                                gapWidth: 0
                            },
                            label: {
                                show: true,
                                position: 'inside',
                                formatter: '{b}',
                                color: '#fff',
                                fontSize: 14,
                                fontWeight: 'bold'
                            }
                        },
                        { // Level 1: RFM combinations within segments
                            itemStyle: {
                                borderWidth: 2,
                                gapWidth: 1,
                                borderColor: '#fff'
                            },
                            label: {
                                show: true,
                                position: 'inside',
                                formatter: '{b}',
                                color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#eee' : '#666',
                                fontSize: 10
                            }
                        }
                    ]
                }
            ]
        };
    }, [filteredData, rfmFilters.Segment, rfmFilters.R, rfmFilters.F]); // Re-calculate if data or any RFM filter changes



    // const scatter3dChartOption = useCallback(() => {

    //     /* ---------------- helpers ---------------- */
    //     const toLog = n => Math.log(Math.max(1, n));

    //     /* ---------------- stats for axes ---------------- */
    //     const rMax = Math.max(...filteredData.map(d => d.Recency));
    //     const fMax = Math.max(...filteredData.map(d => d.Frequency));
    //     const mMax = Math.max(...filteredData.map(d => d.Monetary));

    //     const logRMax = toLog(rMax);
    //     const logFMax = toLog(fMax);
    //     const logMMax = toLog(mMax);

    //     /* ---------------- build one scatter3D series per segment ---------------- */
    //     // Group points by Segment
    //     const segments = Object.keys(segmentColors);



    //     const series = segments.map(segName => ({
    //         name: segName,                          // appears in legend
    //         type: 'scatter3D',
    //         symbolSize: 6,
    //         itemStyle: { color: segmentColors[segName], opacity: 0.8 },
    //         data: filteredData
    //             .filter(d => d.Segment === segName)
    //             .map(d => [
    //                 toLog(d.Recency),
    //                 toLog(d.Frequency),
    //                 toLog(d.Monetary)
    //             ])

    //     }));

    //     /* ---------------- assemble & return option ---------------- */
    //     return {
    //         legend: {
    //             selectedMode: 'multiple',  // single
    //             orient: 'horizontal', // vertical
    //             //left: 0,
    //             //top: 'top', //middle center
    //             top: 10,              // Distance from the top of the chart (adjust as needed)
    //             left: 'center',       // Center align
    //             itemWidth: 14,
    //             itemHeight: 14,
    //             icon: 'circle',
    //             textStyle: { fontSize: 12 },
    //             data: segments.map(name => ({
    //                 name,
    //                 itemStyle: { color: segmentColors[name] }
    //             }))
    //         },

    //         tooltip: {
    //             formatter: ({ value }) => {
    //                 const [logR, logF, logM] = value;
    //                 return (
    //                     `R = ${Math.round(Math.exp(logR))} days<br>` +
    //                     `F = ${Math.round(Math.exp(logF))}<br>` +
    //                     `M = ${formatLargeMonetary(Math.exp(logM))}`
    //                 );
    //             }
    //         },


    //         xAxis3D: {
    //             name: 'Recency',
    //             min: 0,
    //             max: logRMax,
    //             axisLabel: { formatter: v => Math.round(Math.exp(v)) }
    //         },
    //         yAxis3D: {
    //             name: 'Frequency',
    //             min: 0,
    //             max: logFMax,
    //             axisLabel: { formatter: v => Math.round(Math.exp(v)) }
    //         },
    //         zAxis3D: {
    //             name: 'Monetary',
    //             min: 0,
    //             max: logMMax,
    //             axisLabel: { formatter: v => formatLargeMonetary(Math.exp(v)) }
    //         },

    //         grid3D: {
    //             viewControl: { projection: 'perspective', autoRotate: false }
    //         },

    //         series
    //     };
    // }, [filteredData]);

    const scatter3dChartOption = useCallback((theme = 'light') => {
        const isDark = theme === 'dark';

        const toLog = n => Math.log(Math.max(1, n));

        const rMax = Math.max(...filteredData.map(d => d.Recency));
        const fMax = Math.max(...filteredData.map(d => d.Frequency));
        const mMax = Math.max(...filteredData.map(d => d.Monetary));

        const logRMax = toLog(rMax);
        const logFMax = toLog(fMax);
        const logMMax = toLog(mMax);

        const segments = Object.keys(segmentColors);

        const series = segments.map(segName => ({
            name: segName,
            type: 'scatter3D',
            symbolSize: 6,
            itemStyle: {
                color: segmentColors[segName],
                opacity: 0.8
            },
            data: filteredData
                .filter(d => d.Segment === segName)
                .map(d => [toLog(d.Recency), toLog(d.Frequency), toLog(d.Monetary)])
        }));

        const axisColor = isDark ? '#ccc' : '#333';
        const gridLineColor = isDark ? '#666' : '#aaa';
        const tooltipBg = isDark ? '#2d3748' : 'rgba(255, 255, 255, 0.9)';
        const tooltipText = isDark ? '#eee' : '#333';

        return {
            legend: {
                selectedMode: 'multiple',
                orient: 'horizontal',
                top: 10,
                left: 'center',
                itemWidth: 14,
                itemHeight: 14,
                icon: 'circle',
                textStyle: {
                    fontSize: 12,
                    color: axisColor
                },
                data: segments.map(name => ({
                    name,
                    itemStyle: { color: segmentColors[name] }
                }))
            },

            tooltip: {
                backgroundColor: tooltipBg,
                borderColor: gridLineColor,
                borderWidth: 1,
                textStyle: {
                    color: tooltipText
                },
                formatter: ({ value }) => {
                    const [logR, logF, logM] = value;
                    return (
                        `R = ${Math.round(Math.exp(logR))} days<br>` +
                        `F = ${Math.round(Math.exp(logF))}<br>` +
                        `M = ${formatLargeMonetary(Math.exp(logM))}`
                    );
                }
            },

            xAxis3D: {
                name: 'Recency',
                min: 0,
                max: logRMax,
                nameTextStyle: { color: axisColor },
                axisLabel: {
                    formatter: v => Math.round(Math.exp(v)),
                    color: axisColor
                },
                axisLine: {
                    lineStyle: { color: gridLineColor }
                }
            },
            yAxis3D: {
                name: 'Frequency',
                min: 0,
                max: logFMax,
                nameTextStyle: { color: axisColor },
                axisLabel: {
                    formatter: v => Math.round(Math.exp(v)),
                    color: axisColor
                },
                axisLine: {
                    lineStyle: { color: gridLineColor }
                }
            },
            zAxis3D: {
                name: 'Monetary',
                min: 0,
                max: logMMax,
                nameTextStyle: { color: axisColor },
                axisLabel: {
                    formatter: v => formatLargeMonetary(Math.exp(v)),
                    color: axisColor
                },
                axisLine: {
                    lineStyle: { color: gridLineColor }
                }
            },

            grid3D: {
                viewControl: { projection: 'perspective', autoRotate: false },
                axisLine: { lineStyle: { color: gridLineColor } },
                axisPointer: { lineStyle: { color: axisColor } }
            },

            series
        };
    }, [filteredData]);




    const handleRfmChartClick = useCallback((chartName) => (params) => {

        // Start with current filters and clear them if needed
        let newFilters = { ...rfmFilters }; // Make a mutable copy

        // Clear all other filters related to RFM scores and segment when a chart is clicked,
        // to ensure only the selected filter from the clicked chart applies.
        newFilters.R = null;
        newFilters.F = null;
        newFilters.M = null; // Ensure M is also cleared
        newFilters.Segment = null;




        if (chartName === 'rfmGrid') {
            const newRScore = params.value[1] + 1;
            const newFScore = params.value[0] + 1;
            onFilterChange('rfm', {
                R: rfmFilters.R === newRScore ? null : newRScore,
                F: rfmFilters.F === newFScore ? null : newFScore
            });
        } else if (chartName === 'fmHeatmap') {
            // For fmHeatmap, we filter by the specific F and M scores of the clicked cell
            const clickedFScore = params.value[0] + 1; // Assuming value[0] is F-score index
            const clickedMScore = params.value[1] + 1; // Assuming value[1] is M-score index

            onFilterChange('rfm', {
                F: rfmFilters.F === clickedFScore ? null : clickedFScore,
                M: rfmFilters.M === clickedMScore ? null : clickedMScore
            });
        } else if (chartName === 'segments' || chartName === 'monetarySegment') {
            onFilterChange('rfm', {
                Segment: rfmFilters.Segment === params.name ? null : params.name
            });
        } else if (chartName === 'treemap') {
            const clickedName = params.name;
            const parentName = params.data.parent; // Parent name for drilling down
            console.log('clickedName', clickedName);


            const leafMatch = /^R(\d)F(\d)M(\d)$/.exec(clickedName);


            const [, R, F, M] = leafMatch.map(Number);
            const Segment = params.treePathInfo[1].name;


            console.log('newFilters', R, F, M);


            onFilterChange('rfm', {
                R: rfmFilters.R === R ? null : R,
                F: rfmFilters.F === F ? null : F,
                M: rfmFilters.M === M ? null : M
            });





        }
    }, [onFilterChange, rfmFilters]);


    return (
        // Removed id and className related to tab-content. Conditional rendering handles visibility.
        <div>
            {/* Stats Section */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-8">
                <div className="stat-card text-white p-6 rounded-xl shadow-lg">
                    <div className="text-sm font-medium text-indigo-200">Total Customers</div>
                    <div id="total-customers" className="text-3xl font-bold">{totalCustomers.toLocaleString()}</div>
                </div>
                <div className="stat-card text-white p-6 rounded-xl shadow-lg">
                    <div className="text-sm font-medium text-indigo-200">Total Sales</div>
                    <div id="total-sales" className="text-3xl font-bold">{formatLargeMonetary(totalSales)}</div>
                </div>
                <div className="stat-card text-white p-6 rounded-xl shadow-lg">
                    <div className="text-sm font-medium text-indigo-200">Avg. Recency</div>
                    <div id="average-recency" className="text-3xl font-bold">{averageRecency} Days</div>
                </div>
                <div className={`bg-gray-700 text-white p-6 rounded-xl shadow-lg transition-opacity duration-300 ${hasActiveFilter ? 'opacity-100' : 'opacity-0'}`} id="selected-customers-card">
                    <div className="text-sm font-medium text-gray-300">Filtered Customers</div>
                    <div id="selected-customers" className="text-3xl font-bold">{selectedCustomers.toLocaleString()}</div>
                </div>
                <RfmAiInsight filteredData={filteredData} activeFilters={rfmFilters} masterData={masterData} />
            </div>
            <div className="h-4 dark:bg-gray-800"></div>
            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 dark:bg-gray-800">

                <EChartComponent id="rfm-grid-chart" title="Recency Frequency Grid" option={rfmGridOption()} onChartClick={handleRfmChartClick('rfmGrid')} resetRfmFilters={resetRfmFilters} />
                <EChartComponent id="order-count-chart" title="Customers by Order Count" option={orderCountOption()} onChartClick={handleRfmChartClick('orderCount')} resetRfmFilters={resetRfmFilters} />
                <EChartComponent id="segments-chart" title="Unique Customers by Segment" option={segmentsChartOption()} onChartClick={handleRfmChartClick('segments')} resetRfmFilters={resetRfmFilters} />
                <EChartComponent id="monetary-segment-chart" title="Total Sales by Segment" option={monetarySegmentChartOption()} onChartClick={handleRfmChartClick('monetarySegment')} resetRfmFilters={resetRfmFilters} />

                {/* <EChartComponent id="fm-heatmap-chart" title="Frequency Monetary Grid" option={fmHeatmapChartOption()} onChartClick={handleRfmChartClick('fmHeatmap')} /> */}

            </div>


            <EChartComponent id="treemap-chart" title="Customer Segments by RFM Score" option={treemapChartOption()} onChartClick={handleRfmChartClick('treemap')} resetRfmFilters={resetRfmFilters} />

            <div className="h-4 dark:bg-gray-800"></div>

            <EChartComponent id="3d-scatter-chart" title="RFM 3D Scatter Plot (Actual Values)" option={scatter3dChartOption(window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')} onChartClick={handleRfmChartClick('scatter3d')} resetRfmFilters={resetRfmFilters} />
            <div className="h-4 dark:bg-gray-800"></div>
        </div>
    );
};

// ===================================================================
// VoC Dashboard Components
// ===================================================================

const VocAiInsight = ({ data, filters, masterData, product1Data, product2Data }) => {
    let insight = "Select products to compare or apply filters for insights.";
    const isComparing = filters.product1 && filters.product2;

    if (Object.values(filters).some(f => f)) {
        if (data.length < 5) {
            insight = "Not enough data for a meaningful insight with the current filters.";
        } else {
            if (isComparing) {
                insight = `Comparing '${filters.product1}' (${product1Data.length} reviews) and '${filters.product2}' (${product2Data.length} reviews): `;
                const p1Score = product1Data.length > 0 ? product1Data.reduce((a, d) => a + sentimentScores[d.sentiment], 0) / product1Data.length : 0;
                const p2Score = product2Data.length > 0 ? product2Data.reduce((a, d) => a + sentimentScores[d.sentiment], 0) / product2Data.length : 0;
                if (p1Score > p2Score) insight += `'${filters.product1}' has a higher average sentiment score (${p1Score.toFixed(2)}) than '${filters.product2}' (${p2Score.toFixed(2)}). `;
                else insight += `'${filters.product2}' has a higher average sentiment score (${p2Score.toFixed(2)}) than '${filters.product1}' (${p1Score.toFixed(2)}). `;

                let maxDiff = 0;
                let diffTopic = '';
                vocAllTopics.forEach(topic => {
                    const p1TopicData = product1Data.filter(d => d.topic === topic);
                    const p2TopicData = product2Data.filter(d => d.topic === topic);
                    const p1TopicScore = p1TopicData.length > 0 ? p1TopicData.reduce((a, d) => a + sentimentScores[d.sentiment], 0) / p1TopicData.length : 0;
                    const p2TopicScore = p2TopicData.length > 0 ? p2TopicData.reduce((a, d) => a + sentimentScores[d.sentiment], 0) / p2TopicData.length : 0;
                    if (Math.abs(p1TopicScore - p2TopicScore) > maxDiff) {
                        maxDiff = Math.abs(p1TopicScore - p2TopicScore);
                        diffTopic = topic;
                    }
                });
                if (diffTopic) insight += `The largest sentiment difference is in '${diffTopic}'.`;

            } else {
                let sourceName = "the selected criteria";
                if (filters.product1) sourceName = `'${filters.product1}'`;
                else if (filters.product2) sourceName = `'${filters.product2}'`;

                const sentimentCounts = data.reduce((a, d) => { a[d.sentiment] = (a[d.sentiment] || 0) + 1; return a; }, {});
                const dominantSentiment = Object.keys(sentimentCounts).reduce((a, b) => sentimentCounts[a] > sentimentCounts[b] ? a : b, 'Neutral');
                const topicCounts = data.reduce((a, d) => { a[d.topic] = (a[d.topic] || 0) + 1; return a; }, {});
                const topTopic = Object.keys(topicCounts).sort((a, b) => topicCounts[b] - topicCounts[a])[0];
                insight = `For ${sourceName}, the dominant sentiment is '${dominantSentiment}'. The most discussed topic is '${topTopic}'.`;
            }
        }
    }

    return (
        <div className="ai-insight-card text-white p-6 rounded-xl shadow-lg lg:col-span-2">
            <div className="flex justify-between items-center mb-2">
                <div className="text-sm font-medium text-teal-200">AI Generated Insight</div>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-teal-300"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1-1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" /></svg>
            </div>
            <div id="voc-ai-insight-text" className="text-base font-light h-20 overflow-y-auto">{insight}</div>
        </div>
    );
};

const VocDashboard = ({ onFilterChange, vocFilters, resetVocFilters }) => {
    const [masterData, setMasterData] = useState([]);

    // Initialize master data on mount
    useEffect(() => {
        setMasterData(generateVocData(2500));
    }, []);

    // Filter data based on active filters and product comparison
    const baseFilteredData = masterData.filter(d =>
        (vocFilters.sentiment ? d.sentiment === vocFilters.sentiment : true) &&
        (vocFilters.topic ? d.topic === vocFilters.topic : true) &&
        (vocFilters.feature ? d.feature === vocFilters.feature : true)
    );

    const product1Data = vocFilters.product1 ? baseFilteredData.filter(d => d.product === vocFilters.product1) : [];
    const product2Data = vocFilters.product2 ? baseFilteredData.filter(d => d.product === vocFilters.product2) : [];

    const isComparing = vocFilters.product1 && vocFilters.product2;
    let generalChartData;
    if (isComparing) {
        generalChartData = [...product1Data, ...product2Data];
    } else if (vocFilters.product1) {
        generalChartData = product1Data;
    } else if (vocFilters.product2) {
        generalChartData = product2Data;
    } else {
        generalChartData = baseFilteredData;
    }

    // KPI Calculations
    const totalFeedback = generalChartData.length;
    const totalScore = generalChartData.reduce((acc, item) => acc + sentimentScores[item.sentiment], 0);
    const avgSentiment = totalFeedback > 0 ? (totalScore / totalFeedback) : 0;

    const sentimentCounts = generalChartData.reduce((acc, d) => { acc[d.sentiment] = (acc[d.sentiment] || 0) + 1; return acc; }, { Positive: 0, Negative: 0, Neutral: 0 });
    const netSentiment = totalFeedback > 0 ? ((sentimentCounts.Positive - sentimentCounts.Negative) / totalFeedback * 100) : 0;
    const dominantSentiment = Object.keys(sentimentCounts).reduce((a, b) => sentimentCounts[a] > sentimentCounts[b] ? a : b, 'Neutral');

    // Chart Options (memoized for performance)
    const topicSpiderChartOption = useCallback(() => {
        const spiderSeries = [];
        if (vocFilters.product1) {
            const p1Scores = vocAllTopics.map(topic => {
                const topicData = product1Data.filter(d => d.topic === topic);
                return topicData.length > 0 ? topicData.reduce((acc, d) => acc + sentimentScores[d.sentiment], 0) / topicData.length : 0;
            });
            spiderSeries.push({ name: vocFilters.product1, value: p1Scores, lineStyle: { color: '#4f46e5' } });
        }
        if (vocFilters.product2) {
            const p2Scores = vocAllTopics.map(topic => {
                const topicData = product2Data.filter(d => d.topic === topic);
                return topicData.length > 0 ? topicData.reduce((acc, d) => acc + sentimentScores[d.sentiment], 0) / topicData.length : 0;
            });
            spiderSeries.push({ name: vocFilters.product2, value: p2Scores, lineStyle: { color: '#38b2ac' } });
        }
        return {
            tooltip: { trigger: 'item' },
            legend: { data: spiderSeries.map(s => s.name), bottom: '5%' },
            radar: { indicator: vocAllTopics.map(t => ({ name: t, max: 1, min: -1 })), center: ['50%', '50%'], radius: '65%' },
            series: [{ type: 'radar', data: spiderSeries }]
        };
    }, [vocFilters.product1, vocFilters.product2, product1Data, product2Data]);


    const sentimentChartOption = useCallback(() => {
        const currentSentimentCounts = generalChartData.reduce((acc, d) => { acc[d.sentiment] = (acc[d.sentiment] || 0) + 1; return acc; }, {});
        return {
            tooltip: { trigger: 'item' },
            series: [{
                type: 'pie', radius: '70%', center: ['50%', '55%'],
                data: Object.keys(sentimentColors).map(s => ({
                    value: currentSentimentCounts[s] || 0,
                    name: s,
                    itemStyle: { color: vocFilters.sentiment === s ? '#1e40af' : sentimentColors[s] }
                })),
                emphasis: { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0, 0, 0, 0.5)' } }
            }]
        };
    }, [generalChartData, vocFilters.sentiment]);

    const topicChartOption = useCallback(() => {
        const topicCounts = generalChartData.reduce((a, o) => { a[o.topic] = (a[o.topic] || 0) + 1; return a; }, {});
        const sortedTopics = Object.keys(topicCounts).sort((a, b) => topicCounts[b] - topicCounts[a]);
        return {
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
            xAxis: { type: 'category', data: sortedTopics, axisLabel: { interval: 0, rotate: 30 } },
            yAxis: { type: 'value' },
            series: [{
                data: sortedTopics.map(t => ({
                    value: topicCounts[t],
                    name: t,
                    itemStyle: { color: vocFilters.topic === t ? '#1e3a8a' : '#2dd4bf' }
                })),
                type: 'bar'
            }]
        };
    }, [generalChartData, vocFilters.topic]);

    const featureSentimentChartOption = useCallback(() => {
        const featureSentimentData = vocAllFeatures.map(feature => {
            const data = { feature };
            const featureData = generalChartData.filter(d => d.feature === feature);
            Object.keys(sentimentColors).forEach(s => { data[s] = featureData.filter(d => d.sentiment === s).length; });
            return data;
        });
        return {
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
            legend: { data: Object.keys(sentimentColors) },
            dataset: { dimensions: ['feature', ...Object.keys(sentimentColors)], source: featureSentimentData },
            xAxis: { type: 'category' },
            yAxis: { type: 'value' },
            series: Object.keys(sentimentColors).map(s => ({ type: 'bar', stack: 'total', name: s, color: sentimentColors[s], emphasis: { focus: 'series' } }))
        };
    }, [generalChartData]);

    const trendChartOption = useCallback(() => {
        const trendData = generalChartData.reduce((acc, d) => {
            if (!acc[d.date]) acc[d.date] = { Positive: 0, Negative: 0, Neutral: 0 };
            acc[d.date][d.sentiment]++;
            return acc;
        }, {});
        const sortedDates = Object.keys(trendData).sort();
        return {
            tooltip: { trigger: 'axis' },
            legend: { data: Object.keys(sentimentColors) },
            xAxis: { type: 'category', boundaryGap: false, data: sortedDates },
            yAxis: { type: 'value' },
            series: Object.keys(sentimentColors).map(s => ({
                name: s, type: 'line', stack: 'Total',
                data: sortedDates.map(date => trendData[date] ? trendData[date][s] || 0 : 0),
                areaStyle: { color: sentimentColors[s], opacity: 0.5 },
                itemStyle: { color: sentimentColors[s] }
            }))
        };
    }, [generalChartData]);

    const handleVocChartClick = useCallback((filterKey) => (params) => {
        onFilterChange('voc', {
            [filterKey]: vocFilters[filterKey] === params.name ? null : params.name
        });
    }, [onFilterChange, vocFilters]);

    return (
        // Removed id and className related to tab-content. Conditional rendering handles visibility.
        <div>
            {/* KPIs and Comparison Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {/* Product Selectors */}
                <div className="lg:col-span-2 p-6 rounded-xl shadow-lg bg-white">
                    <h3 className="font-semibold text-lg mb-3 text-gray-700">Product Comparison</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="product1-select" className="block text-sm font-medium text-gray-600">Product 1</label>
                            <select
                                id="product1-select"
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                                value={vocFilters.product1 || ''}
                                onChange={(e) => onFilterChange('voc', { product1: e.target.value || null })}
                            >
                                <option value="">Select Product 1</option>
                                {vocAllProducts.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="product2-select" className="block text-sm font-medium text-gray-600">Product 2</label>
                            <select
                                id="product2-select"
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                                value={vocFilters.product2 || ''}
                                onChange={(e) => onFilterChange('voc', { product2: e.target.value || null })}
                            >
                                <option value="">Select Product 2</option>
                                {vocAllProducts.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                    </div>
                    <button onClick={() => onFilterChange('voc', { product1: null, product2: null })} className="mt-3 text-sm text-indigo-600 hover:text-indigo-800">Clear Comparison</button>
                </div>
                {/* AI Insight */}
                <VocAiInsight
                    data={generalChartData}
                    filters={vocFilters}
                    masterData={masterData}
                    product1Data={product1Data}
                    product2Data={product2Data}
                />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="voc-stat-card text-white p-6 rounded-xl shadow-lg">
                    <div className="text-sm font-medium text-teal-200">Total Feedback</div>
                    <div id="total-feedback" className="text-3xl font-bold">{totalFeedback.toLocaleString()}</div>
                </div>
                <div className="voc-stat-card text-white p-6 rounded-xl shadow-lg">
                    <div className="text-sm font-medium text-teal-200">Avg. Sentiment Score</div>
                    <div id="avg-sentiment" className="text-3xl font-bold">{avgSentiment.toFixed(2)}</div>
                </div>
                <div className="voc-stat-card text-white p-6 rounded-xl shadow-lg">
                    <div className="text-sm font-medium text-teal-200">Net Sentiment</div>
                    <div id="net-sentiment" className="text-3xl font-bold">{netSentiment.toFixed(0)}%</div>
                </div>
                <div className="voc-stat-card text-white p-6 rounded-xl shadow-lg">
                    <div className="text-sm font-medium text-teal-200">Dominant Sentiment</div>
                    <div id="dominant-sentiment" className="text-3xl font-bold">{dominantSentiment}</div>
                </div>
            </div>

            {/* VoC Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <EChartComponent id="topic-spider-chart" title="Topic Comparison (Spider Chart)" option={topicSpiderChartOption()} onChartClick={handleVocChartClick('topic')} />
                <EChartComponent id="sentiment-chart" title="Sentiment Distribution" option={sentimentChartOption()} onChartClick={handleVocChartClick('sentiment')} />
                <EChartComponent id="topic-chart" title="Feedback by Topic" option={topicChartOption()} onChartClick={handleVocChartClick('topic')} />
                <EChartComponent id="feature-sentiment-chart" title="Sentiment by Feature" option={featureSentimentChartOption()} onChartClick={handleVocChartClick('feature')} />
                <EChartComponent id="trend-chart" title="Sentiment Trend Over Time" option={trendChartOption()} onChartClick={null} />
            </div>
        </div>
    );
};


// ===================================================================
// Main App Component
// ===================================================================

const EchartDashbaord = ({ custData }) => {

    const [activeTab, setActiveTab] = useState('rfm');
    const [rfmFilters, setRfmFilters] = useState({ R: null, F: null, M: null, Segment: null });
    const [vocFilters, setVocFilters] = useState({ sentiment: null, topic: null, feature: null, product1: null, product2: null });

    // Handles filter changes for both dashboards
    const handleFilterChange = useCallback((dashboard, newFilters) => {
        if (dashboard === 'rfm') {
            setRfmFilters(prev => ({ ...prev, ...newFilters }));
        } else if (dashboard === 'voc') {
            setVocFilters(prev => ({ ...prev, ...newFilters }));
        }
    }, []);

    // Resets all filters for both dashboards
    const handleResetAllFilters = useCallback(() => {
        setRfmFilters({ R: null, F: null, M: null, Segment: null });
        setVocFilters({ sentiment: null, topic: null, feature: null, product1: null, product2: null });
    }, []);

    // Effect to apply custom styles on mount
    useEffect(() => {
        const styleSheet = document.createElement("style");
        styleSheet.type = "text/css";
        styleSheet.innerText = appStyles;
        document.head.appendChild(styleSheet);

        // Clean up the style tag on component unmount
        return () => {
            document.head.removeChild(styleSheet);
        };
    }, []); // Empty dependency array ensures this runs once on mount


    return (
        <div className="antialiased text-gray-800 ">
            {/* Header Section */}

            <div className="container  flex justify-between items-center">
                <h1 className="text-2xl font-bold dark:text-white">Customer Analysis Dashboard</h1>
                {/* <button
                    onClick={handleResetAllFilters}
                    className="bg-indigo-500 hover:bg-indigo-600 text-white font-bold py-2 px-4 rounded-lg transition-colors"
                >
                    Reset All Filters
                </button> */}
            </div>
            <RfmDashboard
                apiData={custData}
                onFilterChange={handleFilterChange}
                rfmFilters={rfmFilters}
                resetRfmFilters={handleResetAllFilters}
            />
            {/* <header className="header p-4 shadow-md sticky top-0 z-50">
                <div className="container mx-auto flex justify-between items-center">
                    <h1 className="text-2xl font-bold">Customer Analysis Dashboard</h1>
                    <button
                        onClick={handleResetAllFilters}
                        className="bg-indigo-500 hover:bg-indigo-600 text-white font-bold py-2 px-4 rounded-lg transition-colors"
                    >
                        Reset All Filters
                    </button>
                </div>
            </header> */}


            {/* <div className="bg-gray-800 sticky top-[68px] z-40">
                <div className="container mx-auto flex">
                    <button
                        className={`tab-button ${activeTab === 'rfm' ? 'active' : ''}`}
                        onClick={() => setActiveTab('rfm')}
                    >
                        RFM Analysis
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'voc' ? 'active' : ''}`}
                        onClick={() => setActiveTab('voc')}
                    >
                        Voice of Customer (VoC)
                    </button>
                </div>
            </div>

            
            <main className="container mx-auto p-4 md:p-6 lg:p-8">
                {activeTab === 'rfm' && (
                    <RfmDashboard
                        apiData={custData}
                        onFilterChange={handleFilterChange}
                        rfmFilters={rfmFilters}
                        resetRfmFilters={handleResetAllFilters}
                    />
                )}
                {activeTab === 'voc' && (
                    <VocDashboard
                        onFilterChange={handleFilterChange}
                        vocFilters={vocFilters}
                        resetVocFilters={handleResetAllFilters}
                    />
                )}
            </main> */}
        </div>
    );
};

export default EchartDashbaord;

